import React, { useState, useEffect, useRef } from 'react';
import './WorldDbUploader.css';
import { database, auth } from '../firebaseConfig';
import { ref, set, push, get, query, orderByChild, equalTo } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, doc, getDocs, setDoc, runTransaction, writeBatch } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';

const WorldDbUploader = ({ onClose, onReload }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mappedColumns, setMappedColumns] = useState({});
  const [fileName, setFileName] = useState('');
  const [csvRows, setCsvRows] = useState([]);
  const [userId, setUserId] = useState(null);
  const [step, setStep] = useState(1); // 1: File selection, 2: Column mapping, 3: Confirmation
  const [delimiter, setDelimiter] = useState(';'); // Default delimiter
  const [importing, setImporting] = useState(false); // Loading state for the import process
  const [importOption, setImportOption] = useState('all'); // 'all' oder 'email_only'
  const [dupCheckComplete, setDupCheckComplete] = useState(false); // Zeigt an, ob die Duplikat-Prüfung abgeschlossen ist
  const [duplicateEmails, setDuplicateEmails] = useState([]); // Liste von doppelten E-Mails
  const [duplicateCount, setDuplicateCount] = useState(0); // Anzahl der doppelten E-Mails
  const [duplicateBusinessNames, setDuplicateBusinessNames] = useState([]); // Liste von doppelten Business-Namen
  const [duplicateBusinessCount, setDuplicateBusinessCount] = useState(0); // Anzahl der doppelten Business-Namen
  const [internalDuplicateCount, setInternalDuplicateCount] = useState(0); // Anzahl der internen Duplikate
  const [showDuplicates, setShowDuplicates] = useState(false); // UI-Status für Anzeige doppelter E-Mails
  const [showDuplicateBusinesses, setShowDuplicateBusinesses] = useState(false); // UI-Status für Anzeige doppelter Business-Namen
  const [filteredData, setFilteredData] = useState([]); // Die gefilterten Daten für den Import
  const [isUploading, setIsUploading] = useState(false); // Der tatsächliche Upload-Status
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Get user ID via Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const availableFields = {
    lead_firstname: 'First Name',
    lead_lastname: 'Last Name',
    lead_email: 'Email',
    lead_telephone: 'Telephone',
    lead_address: 'Address',
    lead_city: 'City',
    lead_postcode: 'Postcode',
    lead_country: 'Country',
    business_name: 'Business Name',
    lead_website: 'Website',
    lead_instagram: 'Instagram',
    lead_linkedin: 'LinkedIn',
    lead_tiktok: 'TikTok',
    lead_rating: 'Total Rating',
    lead_reviews: 'Total Reviews',
    lead_link: 'Link',
    lead_image: 'Image',
    lead_description: 'Description',
    lead_stars: 'Stars',
    lead_type: 'Type',
    lead_facebook: 'Facebook',
    lead_mapsurl: 'Google Maps URL',
    lead_instagram_id: 'Instagram ID',
    lead_instagram_username: 'Instagram Username',
    lead_instagram_followers: 'Instagram Followers',
    lead_instagram_posts: 'Instagram Posts',
    lead_instagram_category: 'Instagram Category'
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Detect the delimiter (comma or semicolon) from the CSV content
  const detectDelimiter = (text) => {
    // Get the first line
    const firstLine = text.split('\n')[0];
    
    // Count occurrences of each delimiter
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    const doubleColonCount = (firstLine.match(/;;/g) || []).length;
    const commaCount = (firstLine.match(/,/g) || []).length;
    
    // Prüfe auf doppelte Trennzeichen (;;)
    if (doubleColonCount > 0 && semicolonCount >= doubleColonCount * 2) {
      return ';;';
    }
    
    // Return the most frequent delimiter
    return semicolonCount >= commaCount ? ';' : ',';
  };

  // Parse CSV with the detected delimiter
  const parseCSV = (text, delimiter) => {
    const lines = text.split('\n');
    const rows = [];
    let currentRow = [];
    let currentValue = '';
    let inQuotes = false;
    
    // Spezielle Behandlung für doppelte Trennzeichen
    const isDoubleDelimiter = delimiter === ';;';
    const actualDelimiter = isDoubleDelimiter ? ';' : delimiter;
    
    if (isDoubleDelimiter) {
      console.log("Detected double delimiter (;;), using special parsing mode");
      
      // Bei doppelten Trennzeichen anders parsen
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line) {
          // Spalte aufteilen und leere Spalten filtern
          const columns = line.split(';;')
            .map(col => col.trim())
            .filter((col, index) => index % 2 === 0 || col !== ''); // Filtere leere Spalten jede zweite Spalte
          
          rows.push(columns);
        }
      }
      
      // Nur wenn Daten vorhanden sind
      if (rows.length === 0) {
        return { header: [], rowsData: [] };
      }
      
      const header = rows[0];
      const rowsData = rows.slice(1);
      
      return { header, rowsData };
    }
    
    // Normale Verarbeitung für einfache Trennzeichen
    // Process each character in the file
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1] || '';
      
      // Handle quotes
      if (char === '"') {
        // If next character is also a quote and we're inside quotes, it's an escaped quote
        if (nextChar === '"' && inQuotes) {
          currentValue += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } 
      // Handle delimiters
      else if (char === actualDelimiter && !inQuotes) {
        currentRow.push(currentValue.trim());
        currentValue = '';
      } 
      // Handle new lines
      else if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !inQuotes) {
        // Add the last value of the current row
        currentRow.push(currentValue.trim());
        
        // Only add non-empty rows
        if (currentRow.some(cell => cell !== '')) {
          rows.push([...currentRow]);
        }
        
        // Reset for next row
        currentRow = [];
        currentValue = '';
        
        // Skip the \n if we just processed \r
        if (char === '\r') {
          i++;
        }
      }
      // Normal character
      else {
        currentValue += char;
      }
    }
    
    // Handle the last row if there's any data
    if (currentValue !== '' || currentRow.length > 0) {
      currentRow.push(currentValue.trim());
      if (currentRow.some(cell => cell !== '')) {
        rows.push([...currentRow]);
      }
    }
    
    // Ensure we have data
    if (rows.length === 0) {
      return { header: [], rowsData: [] };
    }
    
    const header = rows[0].filter(col => col !== '');
    
    // Normalize all rows to have the same number of columns as the header
    const rowsData = rows.slice(1).map(row => {
      // If row has fewer columns than header, pad with empty strings
      if (row.length < header.length) {
        return [...row, ...Array(header.length - row.length).fill('')];
      }
      // If row has more columns than header, truncate
      if (row.length > header.length) {
        return row.slice(0, header.length);
      }
      return row;
    });
    
    return { header, rowsData };
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      setCsvFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          
          // Detect the delimiter
          const detectedDelimiter = detectDelimiter(text);
          setDelimiter(detectedDelimiter);
          
          // Parse the CSV with the detected delimiter
          const { header, rowsData } = parseCSV(text, detectedDelimiter);
          
          if (header.length === 0) {
            alert('No valid data found in the CSV file.');
            return;
          }
          
          setColumns(header);
          setCsvRows(rowsData);
          
          // Vereinfachtes Mapping - nur E-Mail automatisch zuordnen
          const suggestedMappings = {};
          
          // Versuche, eine E-Mail-Spalte zu finden (wichtig für die Duplikatserkennung)
          header.forEach((col, idx) => {
            const lowerCol = col.toLowerCase();
            if (lowerCol.includes('email') || lowerCol === 'mail') {
              suggestedMappings[idx] = 'lead_email';
            }
          });
          
          // Wenn Mapping gefunden wurde, anwenden
          if (Object.keys(suggestedMappings).length > 0) {
            setMappedColumns(suggestedMappings);
          }
          
          setStep(2); // Proceed to column mapping
        } catch (error) {
          console.error('Error parsing CSV file:', error);
          alert('Error parsing CSV file. Please check the format.');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleMappingChange = (index, value) => {
    setMappedColumns((prev) => {
      const newMapping = { ...prev };
      for (const [otherIndex, mappedValue] of Object.entries(newMapping)) {
        if (otherIndex !== index.toString() && mappedValue === value) {
          newMapping[otherIndex] = '';
        }
      }
      newMapping[index] = value;
      return newMapping;
    });
  };

  useEffect(() => {
    if (columns.length) {
      columns.forEach((col, idx) => {
        if (/mail/i.test(col)) {
          setMappedColumns((prev) => {
            const newMapping = { ...prev };
            if (!Object.values(newMapping).includes('lead_email')) {
              newMapping[idx] = 'lead_email';
            }
            return newMapping;
          });
        }
      });
    }
  }, [columns]);

  // Check if at least one field is mapped and if lead_email is mapped (required)
  const atLeastOneFieldAssigned = 
    columns.length > 0 && 
    Object.values(mappedColumns).some(value => value && value.trim() !== "") &&
    Object.values(mappedColumns).includes('lead_email');

  // Parse CSV data into an array of objects based on mapping
  const parseCsvData = () => {
    return csvRows.map((row) => {
      const rowData = {};
      columns.forEach((col, index) => {
        const fieldKey = mappedColumns[index];
        // Only include fields that have been mapped (not empty or "Select...")
        if (fieldKey && fieldKey.trim() !== "") {
          rowData[fieldKey] = row[index] ? row[index].trim() : "";
        }
      });
      return rowData;
    });
  };

  // Neue Hilfsfunktion zur Validierung von E-Mail-Werten
  const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    
    // Trimmen und in Kleinbuchstaben umwandeln
    const normalized = email.trim().toLowerCase();
    
    // Prüfe auf leere E-Mail-Strings oder Platzhalter
    if (normalized === '' || 
        normalized === '0' || 
        normalized === 'null' || 
        normalized === '-' || 
        normalized === 'undefined') {
      return false;
    }
    
    // Einfache E-Mail-Validierung (enthält @)
    return normalized.includes('@');
  };

  // Verbessere die Duplikatsprüfung für E-Mails
  const removeInternalDuplicates = (parsedData) => {
    // Setze internalDuplicateCount auf 0, falls keine Duplikate vorhanden sind
    setInternalDuplicateCount(0);
    
    // Wenn keine Daten vorhanden sind, gibt leeres Array zurück
    if (!parsedData || parsedData.length === 0) {
      return parsedData;
    }
    
    // Debug-Ausgabe der Eingangsdaten
    console.log(`Checking for email duplicates in ${parsedData.length} entries`);
    
    // Nur nach E-Mail-Duplikaten suchen (keine Business-Namen oder andere Felder)
    const emailMap = new Map();
    const uniqueItems = [];
    const duplicates = [];
    
    // Durchlaufe alle Elemente und behalte nur eindeutige E-Mails
    parsedData.forEach((item, index) => {
      // Prüfe, ob das Element eine gültige E-Mail hat
      if (item.lead_email && isValidEmail(item.lead_email)) {
        const normalizedEmail = item.lead_email.trim().toLowerCase();
        
        if (!emailMap.has(normalizedEmail)) {
          // Erste E-Mail dieser Art - behalten
          emailMap.set(normalizedEmail, true);
          uniqueItems.push(item);
        } else {
          // Duplikat gefunden
          duplicates.push({
            email: normalizedEmail,
            index: index
          });
        }
      } else {
        // Ungültige oder fehlende E-Mail = kein Duplikat
        uniqueItems.push(item);
      }
    });
    
    // Setze die Anzahl der gefundenen Duplikate
    const duplicateCount = duplicates.length;
    setInternalDuplicateCount(duplicateCount);
    
    // Debug-Ausgabe
    if (duplicateCount > 0) {
      console.log(`Found ${duplicateCount} internal email duplicates`);
      console.log('Examples of duplicate emails:');
      duplicates.slice(0, 3).forEach(d => console.log(`- Email: ${d.email}, at index: ${d.index}`));
    } else {
      console.log('No internal email duplicates found');
    }
    
    return uniqueItems;
  };

  // Verbessere die Prüfung auf doppelte E-Mail-Adressen in der Datenbank
  const checkDuplicateEmails = async (parsedData) => {
    if (!userId) return [];
    
    try {
      // Extract all valid email addresses from the parsed data
      const emailMap = new Map();
      const dataWithEmails = parsedData.filter(item => 
        item.lead_email && isValidEmail(item.lead_email)
      );
      
      dataWithEmails.forEach(item => {
        const normalizedEmail = item.lead_email.trim().toLowerCase();
        emailMap.set(normalizedEmail, item);
      });
      
      const uniqueEmails = Array.from(emailMap.keys());
      console.log(`Checking ${uniqueEmails.length} unique emails for duplicates in database`);
      
      const duplicates = [];
      
      // Für jede eindeutige E-Mail überprüfen, ob sie bereits in der Datenbank existiert
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      
      // Alle Datenbankeinträge einmal abrufen für bessere Performance
      const allEntriesSnapshot = await getDocs(dbCollectionRef);
      
      if (!allEntriesSnapshot.empty) {
        // Erstelle ein Set aller vorhandenen E-Mail-Adressen (normalisiert)
        const existingEmails = new Set();
        
        allEntriesSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.lead_email && isValidEmail(data.lead_email)) {
            const normalizedDbEmail = data.lead_email.trim().toLowerCase();
            existingEmails.add(normalizedDbEmail);
          }
        });
        
        console.log(`Found ${existingEmails.size} existing emails in database`);
        
        // Jetzt vergleiche alle eindeutigen E-Mails mit den vorhandenen
        for (const email of uniqueEmails) {
          if (existingEmails.has(email)) {
            duplicates.push(email);
          }
        }
      }
      
      console.log(`Found ${duplicates.length} duplicate emails`);
      
      // Debugging: Überprüfe, welche E-Mails nicht als Duplikate erkannt wurden
      if (duplicates.length < uniqueEmails.length) {
        console.log(`${uniqueEmails.length - duplicates.length} emails will be imported:`);
        const nonDuplicateEmails = uniqueEmails.filter(email => !duplicates.includes(email));
        nonDuplicateEmails.forEach(email => {
          console.log(`  - "${email}"`);
        });
      }
      
      // Duplikate-Zähler setzen und Duplikate speichern
      setDuplicateEmails(duplicates);
      setDuplicateCount(duplicates.length);
      
      return duplicates;
    } catch (error) {
      console.error('Error checking for duplicate emails:', error);
      return [];
    }
  };

  // Neue Hilfsfunktion zur Normalisierung von Business-Namen
  const normalizeBusinessName = (name) => {
    if (!name || typeof name !== 'string') return '';
    
    // Entferne extra Leerzeichen, Sonderzeichen und konvertiere zu Kleinbuchstaben
    return name.trim()
      .toLowerCase()
      // Entferne alle Sonderzeichen
      .replace(/[^\w\s]/g, '')
      // Ersetze mehrere Leerzeichen durch ein einzelnes Leerzeichen
      .replace(/\s+/g, ' ');
  };

  // Verbessere die Prüfung auf doppelte Business-Namen
  const checkDuplicateBusinessNames = async (parsedData, emailDuplicates) => {
    if (!userId) return [];
    
    try {
      // Extract all business names from the parsed data
      const businessMap = new Map();
      // Filtere Einträge mit Businesses, die nicht schon durch die E-Mail-Prüfung als Duplikat erkannt wurden
      const dataWithBusinessNames = parsedData.filter(item => {
        const hasBusiness = item.business_name && item.business_name.trim() !== '';
        
        // Falls keine E-Mail vorhanden ist oder die E-Mail nicht in den Duplikaten ist
        const notDuplicateByEmail = !item.lead_email || 
          !isValidEmail(item.lead_email) ||
          !emailDuplicates.includes(item.lead_email.trim().toLowerCase());
        
        return hasBusiness && notDuplicateByEmail;
      });
      
      dataWithBusinessNames.forEach(item => {
        const normalizedName = normalizeBusinessName(item.business_name);
        if (normalizedName) {
          businessMap.set(normalizedName, item);
        }
      });
      
      const uniqueBusinessNames = Array.from(businessMap.keys());
      console.log(`Checking ${uniqueBusinessNames.length} unique business names for duplicates`);
      
      // Verbesserte Methode: Alle DB-Einträge auf einmal abrufen und lokal vergleichen
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      const allEntriesSnapshot = await getDocs(dbCollectionRef);
      const duplicates = [];
      
      if (!allEntriesSnapshot.empty) {
        const existingBusinessNames = new Set();
        
        // Extrahiere alle vorhandenen Business-Namen aus der Datenbank
        allEntriesSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.business_name && data.business_name.trim() !== '') {
            // Normalisiere den Namen zur besseren Vergleichbarkeit
            const normalizedName = normalizeBusinessName(data.business_name);
            if (normalizedName) {
              existingBusinessNames.add(normalizedName);
            }
          }
        });
        
        console.log(`Found ${existingBusinessNames.size} business names in database`);
        
        // Vergleiche jeden Business-Namen aus der CSV mit den existierenden
        for (const businessName of uniqueBusinessNames) {
          if (existingBusinessNames.has(businessName)) {
            duplicates.push(businessName);
          }
        }
      }
      
      console.log(`Found ${duplicates.length} duplicate business names`);
      
      // Debugging: Überprüfe, welche Business-Namen nicht als Duplikate erkannt wurden
      if (duplicates.length < uniqueBusinessNames.length) {
        console.log(`${uniqueBusinessNames.length - duplicates.length} business names will be imported:`);
        const nonDuplicateBusinesses = uniqueBusinessNames.filter(name => !duplicates.includes(name));
        nonDuplicateBusinesses.slice(0, 5).forEach(name => {
          console.log(`  - "${name}"`);
        });
        if (nonDuplicateBusinesses.length > 5) {
          console.log(`  ... and ${nonDuplicateBusinesses.length - 5} more`);
        }
      }
      
      // Duplikate-Zähler setzen und Duplikate speichern
      setDuplicateBusinessNames(duplicates);
      setDuplicateBusinessCount(duplicates.length);
      
      return duplicates;
    } catch (error) {
      console.error('Error checking for duplicate business names:', error);
      return [];
    }
  };

  // Zeige/verstecke Duplikate
  const toggleDuplicates = () => {
    setShowDuplicates(!showDuplicates);
  };

  // Zeige/verstecke doppelte Business-Namen
  const toggleDuplicateBusinesses = () => {
    setShowDuplicateBusinesses(!showDuplicateBusinesses);
  };

  // Modifizierte handleCheckDuplicates-Funktion
  const handleCheckDuplicates = async () => {
    // Check if a user is logged in
    if (!userId) {
      console.error('No user logged in. Data cannot be saved.');
      return;
    }

    // Set importing state to true to show the loading spinner
    setImporting(true);
    setDupCheckComplete(false);
    setInternalDuplicateCount(0); // Reset duplicate count

    // Kleine Verzögerung für UI-Update
    await new Promise(resolve => setTimeout(resolve, 10));

    try {
      // Parse CSV-Daten
      const parsedData = parseCsvData();
      console.log(`Initial data count: ${parsedData.length}`);
      
      // Entferne interne Duplikate
      const uniqueParsedData = removeInternalDuplicates(parsedData);
      console.log(`After removing internal duplicates: ${uniqueParsedData.length}`);

      // Wenn "email_only" Option gewählt wurde, filtere Einträge ohne gültige E-Mail-Adresse
      let filteredByEmailOption = uniqueParsedData;
      if (importOption === 'email_only') {
        filteredByEmailOption = uniqueParsedData.filter(item => 
          item.lead_email && isValidEmail(item.lead_email)
        );
        console.log(`After filtering for valid emails: ${filteredByEmailOption.length}`);
      }

      // Überprüfe auf doppelte E-Mail-Adressen
      const duplicateEmails = await checkDuplicateEmails(filteredByEmailOption);
      
      // Überprüfe auf doppelte Business-Namen (nur für Einträge, die nicht bereits als E-Mail-Duplikate erkannt wurden)
      const duplicateBusinesses = await checkDuplicateBusinessNames(filteredByEmailOption, duplicateEmails);
      
      // Each record gets a timestamp and import name (filename)
      const enhancedData = filteredByEmailOption.map(row => ({
        ...row,
        timestamp: new Date().toISOString(),
        import_name: fileName
      }));

      // Filtere Einträge mit doppelten E-Mail-Adressen oder Business-Namen
      const filteredData = enhancedData.filter(item => {
        // Normale Duplikatslogik für Einträge mit gültiger E-Mail
        if (item.lead_email && isValidEmail(item.lead_email)) {
          const normalizedEmail = item.lead_email.trim().toLowerCase();
          return !duplicateEmails.includes(normalizedEmail);
        }
        
        // Für Einträge ohne gültige E-Mail, aber mit Business-Namen
        if (item.business_name && item.business_name.trim() !== '') {
          const normalizedBusinessName = normalizeBusinessName(item.business_name);
          return !duplicateBusinesses.includes(normalizedBusinessName);
        }
        
        // Für Einträge ohne gültige E-Mail und ohne Business-Namen
        return true; // Diese werden immer importiert, wenn sie bis hierher gekommen sind
      });

      // Debug-Log
      console.log(`Final entries to import: ${filteredData.length}`);
      if (filteredData.length > 0) {
        console.log("Sample entries to be imported:");
        filteredData.slice(0, 3).forEach((item, idx) => {
          console.log(`Item ${idx + 1}:`, 
            item.lead_email ? `Email: ${item.lead_email}` : 'No email', 
            item.business_name ? `Business: ${item.business_name}` : 'No business name'
          );
        });
      }

      // Speichere gefilterte Daten für den späteren Import
      setFilteredData(filteredData);
      
      // Prüfung abgeschlossen, wechsle zum Bestätigungsschritt
      setDupCheckComplete(true);
      setImporting(false);
      setStep(3);
    } catch (error) {
      console.error('Error during duplicate check:', error);
      setImporting(false);
    }
  };

  // Bestätigen und endgültig importieren
  const confirmAndImport = async () => {
    setIsUploading(true);
    setImportProgress(0);
    await new Promise(resolve => setTimeout(resolve, 10));

    // Liste benötigter Referenzen für Rollback
    let importedDocRefs = [];

    try {
      // BEST PRACTICE: Define which fields should be treated as numbers for correct sorting.
      const numericFields = [
        'lead_rating',
        'lead_reviews',
        'lead_stars',
        'lead_instagram_followers',
        'lead_instagram_posts'
      ];

      let successfulImports = 0;

      const CHUNK_SIZE = 500; // 500 Writes pro Batch

      // Hilfsfunktion zum Teilen des Arrays
      const chunkArray = (arr, size) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
          chunks.push(arr.slice(i, i + size));
        }
        return chunks;
      };

      const chunks = chunkArray(filteredData, CHUNK_SIZE);

      for (const chunk of chunks) {
        const batch = writeBatch(firestore);

        for (const row of chunk) {
          // Create a mutable copy of the row data for processing
          const mappedRow = { ...row };

          // 0. Sanitizer für problematische Platzhalterwerte ("0", "null", "NULL")
          const sanitize = (val) => {
            if (val === undefined || val === null) return "";
            const str = String(val).trim();
            if (str === "0" || str.toLowerCase() === "null") return "";
            return str;
          };

          // Speziell sensible Felder bereinigen
          mappedRow.lead_email = sanitize(mappedRow.lead_email);
          mappedRow.lead_website = sanitize(mappedRow.lead_website);
          mappedRow.lead_instagram = sanitize(mappedRow.lead_instagram);

          // 1. Standardwerte für alle Felder setzen, die fehlen
          for (const key in availableFields) {
              if (!mappedRow.hasOwnProperty(key)) {
                  mappedRow[key] = numericFields.includes(key) ? 0 : "";
              }
          }

          // 2. Numerische Felder konvertieren (falls sie als String gespeichert waren)
          for (const field of numericFields) {
              if (mappedRow.hasOwnProperty(field)) {
                  const value = parseFloat(mappedRow[field]);
                  mappedRow[field] = isNaN(value) ? 0 : value;
              }
          }

          // 3. Normalisierte Felder für die Suche hinzufügen
          // Business-Name und Description zusammenführen (in Kleinbuchstaben) für eine vereinheitlichte Suche
          const namePart = mappedRow.business_name ? mappedRow.business_name.toLowerCase() : "";
          const descPart = mappedRow.lead_description ? mappedRow.lead_description.toLowerCase() : "";
          mappedRow.business_name_normalized = `${namePart} ${descPart}`.trim();

          // 4. Standard-Status setzen, falls nicht vorhanden
          if (!mappedRow.hasOwnProperty('lead_contacted')) {
              mappedRow.lead_contacted = "Not Contacted";
          }

          // 5. Felder für spezielle Filter hinzufügen
          const hasWebsite = !!(mappedRow.lead_website && mappedRow.lead_website.trim());
          const emailValue = mappedRow.lead_email;
          const hasEmail = !!(emailValue && typeof emailValue === 'string' && /\S+@\S+\.\S+/.test(emailValue));
          const hasInstagram = !!(mappedRow.lead_instagram && mappedRow.lead_instagram.trim());
          
          mappedRow.is_ready_for_spy = hasWebsite && !hasEmail;
          mappedRow.has_website = hasWebsite;
          mappedRow.has_email = hasEmail;
          mappedRow.has_instagram = hasInstagram;

          // Add timestamp and import name
          mappedRow.timestamp = new Date().toISOString();
          mappedRow.import_name = fileName;

          // -------- Race-safe write using deterministic ID within same transaction --------
          const normalizedEmail = hasEmail ? emailValue.trim().toLowerCase() : null;
          const normalizedBiz = mappedRow.business_name ? normalizeBusinessName(mappedRow.business_name) : null;

          let docRef;
          if (normalizedEmail) {
            docRef = doc(firestore, `World/${userId}/db`, `e_${normalizedEmail}`);
          } else if (normalizedBiz) {
            docRef = doc(firestore, `World/${userId}/db`, `b_${normalizedBiz}`);
          } else {
            docRef = doc(collection(firestore, `World/${userId}/db`));
          }

          batch.set(docRef, mappedRow);
          importedDocRefs.push(docRef);
        }

        await batch.commit();
        successfulImports += chunk.length;
        setImportProgress(Math.round((successfulImports / filteredData.length) * 100));
      }

      // After all writes, store import metadata
      if (fileName) {
        const importNameRef = doc(firestore, `World/${userId}/import_names`, fileName);
        await setDoc(importNameRef, { name: fileName, last_imported: new Date().toISOString() });
      }

      // Refresh page after import
      window.location.reload();

    } catch (error) {
      console.error('Error importing data:', error);
      // Rollback: versuche alle bereits importierten Dokumente zu löschen
      try {
        const BATCH_DELETE_SIZE = 500;
        for (let i = 0; i < importedDocRefs.length; i += BATCH_DELETE_SIZE) {
          const slice = importedDocRefs.slice(i, i + BATCH_DELETE_SIZE);
          const batchDel = writeBatch(firestore);
          slice.forEach(refDoc => batchDel.delete(refDoc));
          await batchDel.commit();
        }
        console.log(`Rollback successful – removed ${importedDocRefs.length} docs`);
      } catch (rollbackErr) {
        console.error('Rollback failed:', rollbackErr);
      }

      alert('An error occurred during import. No contacts were imported. Please try again.');
    } finally {
      setIsUploading(false);
      setImporting(false);
    }
  };

  // Zurück zur Spaltenzuordnung
  const goBackToMapping = () => {
    setStep(2);
    setDupCheckComplete(false);
    setDuplicateCount(0);
    setDuplicateEmails([]);
    setDuplicateBusinessCount(0);
    setDuplicateBusinessNames([]);
  };

  // Berechne die Gesamtzahl der zu importierenden Einträge
  const totalImportCount = filteredData.length;
  const totalSkippedCount = duplicateCount + duplicateBusinessCount;

  return (
    <div className="world-db-uploader-overlay">
      <div className="world-db-uploader-popup" ref={modalRef}>
        <button className="world-db-uploader-close" onClick={onClose}>×</button>
  
        
        {step === 1 ? (
          <div className="world-db-uploader-step1">
            <div className="world-db-uploader-icon-container">
              <img src="/img/upload.svg" alt="Upload Icon" className="world-db-uploader-icon" />
            </div>
            <h2>Upload File</h2>
            <p>Select a CSV or TXT file to import your contacts</p>
            <button className="world-db-uploader-select-btn" onClick={triggerFileInput}>
              Select File
            </button>
            <input 
              type="file" 
              ref={fileInputRef}
              accept=".csv,.txt" 
              onChange={handleFileChange} 
              style={{ display: 'none' }}
            />
          </div>
        ) : step === 2 ? (
          <div className="world-db-uploader-step2">
            <h2>Column Mapping</h2>
            <p className="world-db-uploader-filename">
              File: {fileName} <span className="delimiter-info">(Delimiter: {delimiter === ';;' ? 'Double Semicolon' : delimiter === ';' ? 'Semicolon' : 'Comma'})</span>
            </p>
            
            {delimiter === ';;' && (
              <div className="world-db-delimiter-hint">
                <div className="world-db-delimiter-hint-header">
                  <span><i className="fa fa-info-circle"></i> Double Semicolon Delimiter Detected</span>
                </div>
                <p>
                  This file uses double semicolons (;;) as a delimiter. 
                  We have automatically removed the empty columns.
                </p>
              </div>
            )}
            
            <div className="world-db-mapping-container">
              {columns.map((col, index) => (
                <div key={index} className="world-db-mapping-row">
                  <span className="world-db-column-name">{col}</span>
                  <select
                    onChange={(e) => handleMappingChange(index, e.target.value)}
                    value={mappedColumns[index] || ''}
                  >
                    <option value="">Select...</option>
                    {Object.entries(availableFields).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="world-db-import-options">
              <h3>Import Options</h3>
              <div className="world-db-import-option-row">
                <label>
                  <input 
                    type="radio" 
                    name="importOption" 
                    value="all" 
                    checked={importOption === 'all'} 
                    onChange={() => setImportOption('all')} 
                  />
                  Import all entries (regardless of email)
                </label>
              </div>
              <div className="world-db-import-option-row">
                <label>
                  <input 
                    type="radio" 
                    name="importOption" 
                    value="email_only" 
                    checked={importOption === 'email_only'} 
                    onChange={() => setImportOption('email_only')} 
                  />
                  Import only entries with email
                </label>
              </div>
            </div>

            <div className="world-db-uploader-actions">
              <button 
                className="world-db-uploader-back-btn" 
                onClick={() => setStep(1)}
                disabled={importing}
              >
                Back
              </button>
              <button
                className={`world-db-uploader-submit-btn ${
                  atLeastOneFieldAssigned ? 'active' : 'inactive'
                } ${importing ? 'loading' : ''}`}
                onClick={handleCheckDuplicates}
                disabled={!atLeastOneFieldAssigned || importing}
              >
                {importing ? 'Checking duplicates...' : 'Check Duplicates'}
              </button>
            </div>
          </div>
        ) : (
          <div className="world-db-uploader-step3">
            <h2>Import Preview</h2>
            <p className="world-db-uploader-filename">
              File: {fileName} <span className="delimiter-info">({csvRows.length} total entries)</span>
            </p>
            
            {duplicateCount > 0 && (
              <div className="world-db-duplicate-info">
                <div className="world-db-duplicate-header">
                  <span className="world-db-duplicate-count">
                    {duplicateCount} {duplicateCount === 1 ? 'duplicate' : 'duplicates'} with identical email found
                  </span>
                  <button 
                    className="world-db-toggle-duplicates-btn"
                    onClick={toggleDuplicates}
                  >
                    {showDuplicates ? 'Hide' : 'Show'}
                  </button>
                </div>
                
                {showDuplicates && (
                  <div className="world-db-duplicate-list">
                    {duplicateEmails.map((email, index) => (
                      <div key={index} className="world-db-duplicate-item">{email}</div>
                    ))}
                  </div>
                )}

                <p className="world-db-duplicate-message">
                  These email addresses already exist in the database and will be skipped.
                </p>
              </div>
            )}

            {duplicateBusinessCount > 0 && (
              <div className="world-db-duplicate-info business">
                <div className="world-db-duplicate-header">
                  <span className="world-db-duplicate-count">
                    {duplicateBusinessCount} {duplicateBusinessCount === 1 ? 'duplicate' : 'duplicates'} with identical business name found
                  </span>
                  <button 
                    className="world-db-toggle-duplicates-btn"
                    onClick={toggleDuplicateBusinesses}
                  >
                    {showDuplicateBusinesses ? 'Hide' : 'Show'}
                  </button>
                </div>
                
                {showDuplicateBusinesses && (
                  <div className="world-db-duplicate-list">
                    {duplicateBusinessNames.map((businessName, index) => (
                      <div key={index} className="world-db-duplicate-item">{businessName}</div>
                    ))}
                  </div>
                )}

                <p className="world-db-duplicate-message">
                  These business names already exist in the database and will be skipped.
                </p>
              </div>
            )}

            <div className="world-db-import-summary">
              <h3>Import Summary</h3>
              <div className="world-db-summary-stats">
                <div className="world-db-summary-row">
                  <span className="world-db-summary-label">Total entries:</span>
                  <span className="world-db-summary-value">{csvRows.length}</span>
                </div>
                {internalDuplicateCount > 0 && (
                  <div className="world-db-summary-row">
                    <span className="world-db-summary-label">Internal duplicates:</span>
                    <span className="world-db-summary-value negative">{internalDuplicateCount}</span>
                  </div>
                )}
                {importOption === 'email_only' && (
                  <div className="world-db-summary-row">
                    <span className="world-db-summary-label">Entries without valid email:</span>
                    <span className="world-db-summary-value negative">
                      {csvRows.filter(row => {
                        const emailIndex = Object.keys(mappedColumns).find(
                          key => mappedColumns[key] === 'lead_email'
                        );
                        if (!emailIndex || !row[emailIndex]) return true;
                        const emailValue = row[emailIndex].trim();
                        return !isValidEmail(emailValue);
                      }).length}
                    </span>
                  </div>
                )}
                <div className="world-db-summary-row">
                  <span className="world-db-summary-label">Email duplicates:</span>
                  <span className="world-db-summary-value negative">{duplicateCount}</span>
                </div>
                <div className="world-db-summary-row">
                  <span className="world-db-summary-label">Business name duplicates:</span>
                  <span className="world-db-summary-value negative">{duplicateBusinessCount}</span>
                </div>
                <div className="world-db-summary-row total">
                  <span className="world-db-summary-label">Entries to import:</span>
                  <span className="world-db-summary-value positive">{totalImportCount}</span>
                </div>
                <div className="world-db-summary-row import-option">
                  <span className="world-db-summary-label">Import option:</span>
                  <span className="world-db-summary-value">
                    {importOption === 'all' ? 'All entries' : 'Only entries with email'}
                  </span>
                </div>
              </div>
              
              {totalImportCount > 0 && duplicateCount > 0 && (
                <div className="world-db-duplicate-hint">
                  <p>
                    <strong>Note:</strong> If you've recently imported the same data and still see entries to import, 
                    there might be slight differences in the format of email addresses or business names. 
                    Check the console for detailed information.
                  </p>
                </div>
              )}
            </div>

            <div className="world-db-uploader-actions">
              <button 
                className="world-db-uploader-back-btn" 
                onClick={goBackToMapping}
                disabled={isUploading}
              >
                Back to Mapping
              </button>
              <button
                className={`world-db-uploader-submit-btn ${
                  totalImportCount > 0 ? 'active' : 'inactive'
                } ${isUploading ? 'loading' : ''}`}
                onClick={confirmAndImport}
                disabled={totalImportCount === 0 || isUploading}
              >
                {isUploading ? 'Importing data...' : `Import Data (${totalImportCount})`}
              </button>
              
            </div>
                  
        {isUploading && (
          <div className="import-progress-bar-container">
            <div className="import-progress-bar" style={{ width: `${importProgress}%` }} />
            <div className="import-progress-label">{importProgress}%</div>
          </div>
        )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorldDbUploader;
